<template>
  <div>
    <ModalBase :show-modal="true" @close-modal="close" :auto-h="true" :narrow="true" :overlay-scrollb="false" class="heroes-modal">
      <template v-slot:titlebar>
        <TitleBar variant-class="title-modal title-modal--simple">
          <template v-slot:title>
            Gekko Hero Program
          </template>
        </TitleBar>
      </template>
      <template v-slot:maincontent>
        <p class="modal-intro" v-translate>
          Give a discount, earn money.
        </p>
        <p>
          <ul class="heroes-steps">
            <li v-translate>You have been a premium user for so long that you can now use the Gekko Hero code below to invite new people to Gekko</li>
            <li v-translate>If people use your code to signup to a free Gekko Basic account, they will get a discount of more than 35% if they upgrade to a paid Premium account</li>
            <li v-translate='{ discount_amount: 20 }'>
              For each user that upgrades within 20 days after signup using your code, you will automatically receive €%{discount_amount} in your bank account (*)
            </li>
          </ul>
        </p>
        <div v-if="isGrace">
          <div class="heroes-link heroes-grace mb-4">
            {{ $gettext('You are about to lose your hero cape.') }}
            <br>
            {{ $gettext('Upgrade now to keep your hero status.') }}
          </div>
        </div>
        <div v-if="isCancelled">
          <div class="heroes-link heroes-cancel mb-4">
            {{ $gettext('Your Heroes promo code has expired.') }}
            <br>
            {{ $gettext('Become a hero again to be able to give discounts to other people.') }}
          </div>
        </div>
        <div v-else>
          <div v-if="promoData.referral_code" class="heroes-link">
            {{ promoUrl }}
            <GekkoButton 
              @click="copyToClipboard(promoUrl)"
              @mouseleave.native="resetCopiedText()"
              id="copy-to-clipboard"
              :text="$gettext('Copy link')"
              class="ml-2"
              size="sm"
            />
            <b-tooltip target="copy-to-clipboard" triggers="hover">
              <div class="tooltip-text">
                {{ copyToClipboardText }}
              </div>
            </b-tooltip>
          </div>

          <div v-if="!isCancelled" class="promo-overview">
            <div class="promo-overview-data">
              <div class="promo-overview-data-item">
                <span class="amount">{{ amountEarned }}</span>
                <span class="label">{{ $gettext('Earned') }}</span>
              </div>
              <div class="promo-overview-data-item">
                <span class="amount">{{ (promoData.total_users_created) ? promoData.total_users_created : 0 }}</span>
                <span class="label">{{ $gettext('Invites active') }}</span>
              </div>
            </div>
          </div>
        </div>

        <p class="heroes-disclaimer" v-translate='{ target_iban: promoData.iban }'>
          (*) Your earnings will automatically be transferred to the account known to Gekko (%{target_iban}). If your account is downgraded and your hero status revoked, your invite code will become void and any outstanding invites will no longer be credited to you. Please contact support@getgekko.com in case of any questions about the Gekko Hero Program and the terms and conditions.
        </p>
      </template>

      <template v-slot:actions>
        <GekkoButton 
          @click="close()" 
          :text="$gettext('Close')" 
          variant="link"
          class="text-primary px-0 ml-auto"
        />
      </template>
    </ModalBase>
  </div>
</template>

<script>
import GekkoButton from '@/components/GekkoButton';
import ModalBase from "@/components/base_components/ModalBase";
import TitleBar from "@/components/TitleBar";
import { mapGetters } from "vuex";

export default {
    name: "HeroesModal",
    components: { GekkoButton, ModalBase, TitleBar },
    props: {
      bonus: {
        type: Number,
        default: 0
      },
      promoData: {
        type: Object,
        default: () => {}
      },
      promoStatus: {
        type: String,
        default: ''
      }
    },
    data () {
        return {
          showCopiedText: false
        }
    },
    computed: {
      ...mapGetters({ 
        currentCompany: "main/getCurrentCompany"
      }),
      promoUrl () {
        let url = ''
        if (this.promoData) {
          url = 'https://getgekko.com/en/heroesinvite/' + this.promoData.referral_code
        }
        return url
      },
      copyToClipboardText () {
        return (this.showCopiedText) ? this.$gettext('Copied!') : this.$gettext('Copy to clipboard')
      },
      amountEarned () {
        let amount = '0,-'
        if (this.promoData && this.promoData.total_users_paying > 0) {
          amount = this.promoData.total_users_paying * this.bonus
        }
        return '€' + amount
      },
      isCancelled () {
        return this.promoStatus === 'cancel'
      },
      isGrace () {
        return this.promoStatus === 'grace'
      }
    },
    created () {
      document.body.classList.add('modal-on')
    },
    methods: {
      close() {
        this.$emit('close-modal', false);
        document.body.classList.remove('modal-on')
      },
      copyToClipboard (data) {
        navigator.clipboard.writeText(data).then(() => {
          this.showCopiedText = true
        }, (err) => {
          console.error('Could not copy: ', err)
        })
      },
      resetCopiedText () {
        setTimeout(() => {
          this.showCopiedText = false
        }, 100)
      },
    }
}
</script>

<style lang="scss">
  .heroes-modal {
    font-size: 16px;
    color: #525f7f;
    .gk-modal {
      max-width: 570px !important;
    }
    .titlebar {
      margin-bottom: 0.5rem !important;
      width: 100% !important;
      border-bottom: 1px solid $colorBorderDash;
      .title-wrap {
        justify-content: center;
      }
      .action-bar { display: none !important; }
      h2 { 
        color: $colorBrand;
        font-size: 20px !important;
        text-transform: uppercase;
        padding: 1rem;
        padding-top: 0.5rem;
      }
    }
    .action-buttons-wrapper {
      border-top: 1px solid $colorBorderDash;
      margin-top: 0.5rem !important;
      text-align: center;
    }
    .modal-intro {
      font-size: 20px;
      text-align: center;
      padding-top: 1rem;
      font-family: 'DINNextLTPro-Medium';
    }
    .heroes-steps {
      list-style: none;
      padding: 1rem 2rem;
      counter-reset: hero-counter;
      padding-left: 4rem;
      li {
        font-size: 15px; 
        margin: 0 0 1rem 0;
        counter-increment: hero-counter;
        position: relative;
        .iban {
          white-space: nowrap;
          color: $colorBrand;
        }
        &:before {
          content: counter(hero-counter);
          color: white;
          position: absolute;
          --size: 25px;
          left: calc(-1 * var(--size) - 10px);
          line-height: var(--size);
          width: var(--size);
          height: var(--size);
          top: 0;
          background: $colorBrand;
          border-radius: 50%;
          text-align: center;
        }
      }
    }
    .modal-content-wrap {
      padding: 0rem 2rem;
    }
    .heroes-bg {
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      // object-fit: contain;
      display: none;
      opacity: 0.3;
    }

    .promo-overview {
      padding: 1rem 0;
      .promo-overview-data {
        display: flex;
        flex-wrap: warp;
        justify-content: center;
        align-content: center;
        padding: 1rem 0;
        gap: 1rem;
        .promo-overview-data-item {
          width: 33%;
          text-align: center;
          padding: 0.75rem 0;
          border-radius: 12px;
          border: 2px solid $colorBorderDash;
          border-bottom: 4px solid $colorBorderDash;
          span.label {
            display: block;
            font-size: 14px;
            color: #8898aa;
          }
          span.amount {
            display: block;
            font-size: 32px;
            color: #525f7f;
            line-height: 1.2;
            font-family: 'DINNextLTPro-Medium';
          }
        }
      }
    }

    .heroes-link {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem;
      border-radius: 8px;
      background: rgba(105,114,222,.09);
      color: #525f7f;
      text-align: center;
      font-size: 1rem;
      flex-wrap: wrap;
      font-size: 15px;
    }
    .heroes-grace {
      background: $colorWarningBg;
      color: $colorWarningText;
      border: 1px solid $colorWarningBorder;
      justify-content: center;
    }
    .heroes-cancel {
      background: $colorDangerBg;
      color: $colorDangerText;
      border: 1px solid $colorDangerBorder;
      justify-content: center;
    }
    .heroes-disclaimer {
      font-size: 12px;
      color: #b9b4b4;
      text-align: center;
    }
  }
</style>

<style lang="scss" scoped>
.info-text {
  font-style: italic;
  color: $colorTextSemi;
  font-size: 14px;
  margin-bottom: 1rem;
}
</style>
